let blockObserver = null;
const classes = {
    block: 'pxu-lia-block',
    element: 'pxu-lia-element',
    playBlock: 'pxu-lia-block--play',
    resetBlock: 'pxu-lia-block--reset',
    playSection: 'pxu-lia-section--play',
    resetSection: 'pxu-lia-section--reset',
};
const mappingSelector = '[type="application/pxs-animation-mapping+json"]';
// Find correct DOM elements using `mappingSelector` to add the 'block' and 'element' classes to.
const addClasses = (parentEl) => {
    parentEl
        .querySelectorAll(mappingSelector)
        .forEach(mappingEl => {
        const section = mappingEl.parentNode;
        try {
            const mapping = JSON.parse(mappingEl.innerHTML);
            mapping.elements.forEach((elementSelector) => {
                section
                    .querySelectorAll(elementSelector)
                    .forEach(element => element.classList.add(classes.element));
            });
            mapping.blocks.forEach((blockSelector) => {
                section
                    .querySelectorAll(blockSelector)
                    .forEach(block => block.classList.add(classes.block));
            });
        }
        catch (error) {
            console.warn('Unable to parse animation mapping', mappingEl, error);
        }
    });
};
// Find common parent of this block and the closest animation mapping block.
const getMappingParentFromBlock = ({ parentElement }) => (parentElement.querySelector(mappingSelector)
    ? parentElement
    : getMappingParentFromBlock(parentElement));
const reset = (block, elements) => {
    block.style.animationName = 'none';
    block.classList.add(classes.resetBlock);
    elements.forEach(element => { element.style.animationName = 'none'; });
};
const removeReset = (block, elements) => {
    block.style.animationName = '';
    block.classList.remove(classes.resetBlock);
    elements.forEach(element => { element.style.animationName = ''; });
};
const play = (block, elements) => {
    block.style.animationPlayState = 'running';
    block.classList.add(classes.playBlock);
    elements.forEach(element => { element.style.animationPlayState = 'running'; });
};
const pause = (block, elements) => {
    block.style.animationPlayState = '';
    block.classList.remove(classes.playBlock);
    elements.forEach(element => { element.style.animationPlayState = ''; });
};
// Play, or reset animations for a collection of blocks.
const loadInAnimations = (blocks, state = 'play') => {
    const callNextFrame = [];
    if (state === 'reset') {
        blocks.forEach(block => {
            const elements = block.querySelectorAll(`.${classes.element}`);
            pause(block, elements);
            reset(block, elements);
            callNextFrame.push(() => {
                removeReset(block, elements);
            });
        });
        const section = getMappingParentFromBlock(blocks[0]);
        section.classList.remove(classes.playSection);
        section.classList.add(classes.resetSection);
        callNextFrame.push(() => {
            section.classList.remove(classes.resetSection);
        });
    }
    else {
        blocks.forEach((block, blockIndex) => {
            // Set sequence for blocks
            block.style.setProperty('--pxu-lia-outer-sequence', `${blockIndex}`);
            // Set sequence for sub elements (heading, text, button, etc)
            const elements = block.querySelectorAll(`.${classes.element}`);
            elements.forEach((element, elementIndex) => {
                element.style.setProperty('--pxu-lia-inner-sequence', `${elementIndex}`);
            });
            if (block.classList.contains(classes.playBlock)) {
                reset(block, elements);
                callNextFrame.push(() => {
                    removeReset(block, elements);
                    play(block, elements);
                });
            }
            else {
                play(block, elements);
            }
            const section = getMappingParentFromBlock(blocks[0]);
            if (section.classList.contains(classes.playSection)) {
                section.classList.add(classes.resetSection);
                callNextFrame.push(() => {
                    section.classList.remove(classes.resetSection);
                    section.classList.add(classes.playSection);
                });
            }
            else {
                section.classList.add(classes.playSection);
            }
        });
    }
    window.requestAnimationFrame(() => callNextFrame.forEach(fn => fn()));
};
// Manually trigger load-in animations. Existing animations will be reset and replayed.
const playLoadInAnimations = (blocks) => loadInAnimations(blocks, 'play');
// Manually reset load-in animations to initial state.
const resetLoadInAnimations = (blocks) => loadInAnimations(blocks, 'reset');
const intersectionCallback = (entries, observer) => {
    const toAnimate = new Map();
    entries.forEach(entry => {
        if (entry.isIntersecting) {
            // Animations only run once so stop observing
            observer.unobserve(entry.target);
            // Find all blocks on same level that are now intersecting and arrange by parent section
            const parentSequence = getMappingParentFromBlock(entry.target);
            const existingSiblings = toAnimate.get(parentSequence);
            toAnimate.set(parentSequence, existingSiblings ? [...existingSiblings, entry.target] : [entry.target]);
        }
    });
    toAnimate.forEach((blocks, _parent) => playLoadInAnimations(blocks));
};
// Remove blocks from the global intersection observer for automatic load-in animations.
const removeLoadInAnimationsAutoplay = (blocks) => (blocks.forEach(block => blockObserver === null || blockObserver === void 0 ? void 0 : blockObserver.unobserve(block)));
const initLoadInAnimationsAutoplay = (options = { threshold: 0.3 }, parentEl = document) => {
    if (!('reduceAnimations' in document.body.dataset)) {
        addClasses(parentEl);
        /*
          This assumes that animated elements are arranged by section, block, and element.
          Animations are triggered when blocks enter view,
          and are sequenced within sections by block and by subElement.
          Sequencing information is applied as custom properties that can be referenced in CSS.
        */
        blockObserver = new IntersectionObserver(intersectionCallback, options);
        const observe = (el) => {
            el
                .querySelectorAll(`.${classes.block}`)
                .forEach((block) => blockObserver === null || blockObserver === void 0 ? void 0 : blockObserver.observe(block));
        };
        observe(parentEl);
    }
};
export { initLoadInAnimationsAutoplay, playLoadInAnimations, resetLoadInAnimations, removeLoadInAnimationsAutoplay, };
